import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DownloadSimple, EnvelopeSimple, Receipt } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";

const SYM = { GBP: "£", USD: "$", EUR: "€", CAD: "$", AUD: "$", NZD: "$", INR: "₹", JPY: "¥" };
const money = (n, c) => `${SYM[(c || "GBP").toUpperCase()] ?? ""}${Number(n || 0).toFixed(2)}`;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function GroupPaymentsSection() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 0 = whole year
  const [includeCredits, setIncludeCredits] = useState(true);
  const [data, setData] = useState(null);
  const [months, setMonths] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/group/payments/months").then(({ data }) => setMonths(data.months)).catch(() => {}); }, []);
  useEffect(() => {
    setData(null);
    api.get("/group/payments/export", { params: { year, month: month || undefined, include_credits: includeCredits } })
      .then(({ data }) => setData(data)).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
  }, [year, month, includeCredits]);

  const years = Array.from(new Set([now.getFullYear(), ...months.map((m) => Number(m.slice(0, 4)))])).sort((a, b) => b - a);
  const hasData = (y, m) => months.includes(`${y}-${String(m).padStart(2, "0")}`);

  const download = async () => {
    setBusy("csv");
    try {
      const res = await api.get("/group/payments/export", { params: { year, month: month || undefined, include_credits: includeCredits, format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `traina-payments-${year}${month ? `-${String(month).padStart(2, "0")}` : ""}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed"); }
    finally { setBusy(null); }
  };
  const email = async () => {
    setBusy("email");
    try { const { data: r } = await api.post("/group/payments/export/email", { year, month: month || null, include_credits: includeCredits }); toast.success(`Sent ${r.rows} rows for ${r.period} to ${r.to}`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="group-payments-section">
      <div className="flex flex-wrap items-center gap-2">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="px-3 py-2 border text-sm" data-testid="payments-year">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="px-3 py-2 border text-sm" data-testid="payments-month">
          <option value={0}>Whole year</option>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}{hasData(year, i + 1) ? " ●" : ""}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs text-zinc-400 ml-2 cursor-pointer">
          <input type="checkbox" checked={includeCredits} onChange={(e) => setIncludeCredits(e.target.checked)} className="accent-brand-light" data-testid="payments-include-credits" />
          Include credit-paid bookings (£0 rows)
        </label>
        <div className="ml-auto flex gap-2">
          <button onClick={download} disabled={busy || !data} className="btn-ghost !py-2 !px-3 !text-[10px]" data-testid="payments-download-csv"><DownloadSimple size={12} /> Download CSV</button>
          <button onClick={email} disabled={busy || !data} className="btn-primary !py-2 !px-3 !text-[10px]" data-testid="payments-email-accountant" title={data?.accountant_email ? `Sends to ${data.accountant_email}` : "Add your accountant's email in Settings"}>
            <EnvelopeSimple size={12} /> Email to my accountant
          </button>
        </div>
      </div>
      {data && !data.accountant_email && (
        <p className="text-xs text-zinc-500" data-testid="payments-no-accountant">No accountant email saved yet — add one under <Link to="/trainer/settings" className="text-brand underline">Settings → Branding</Link>.</p>
      )}

      {!data ? <p className="tracker text-zinc-500">Loading…</p> : (
        <>
          <div className="grid sm:grid-cols-4 gap-3" data-testid="payments-summary">
            {[["GROSS", money(data.summary.gross, data.currency)], ["PLATFORM FEES", money(data.summary.fees, data.currency)], ["NET", money(data.summary.net, data.currency)], ["PAYMENTS", `${data.summary.count} · ${data.summary.classes} classes / ${data.summary.packs} packs`]].map(([k, v]) => (
              <div key={k} className="card-flat p-4"><div className="tracker text-[9px] text-zinc-500">{k}</div><div className="font-display text-2xl mt-1">{v}</div></div>
            ))}
          </div>
          <div className="tracker text-[10px] text-zinc-500">{data.period} · {data.summary.card_count} card · {data.summary.credit_count} credit</div>
          {data.rows.length === 0 ? (
            <div className="card-flat p-10 text-center" data-testid="payments-empty">
              <Receipt size={28} weight="duotone" className="text-brand mx-auto mb-3" />
              <p className="font-display text-xl">No payments in this period.</p>
            </div>
          ) : (
            <div className="border border-white/10 overflow-x-auto" data-testid="payments-table">
              <table className="w-full text-xs">
                <thead className="tracker text-[9px] text-zinc-500 text-left">
                  <tr>{["Date", "Receipt", "Type", "Item", "Member", "Amount", "Fee", "Net", "Method"].map((h) => <th key={h} className="px-3 py-2 border-b border-white/10 font-normal">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {data.rows.map((r) => (
                    <tr key={r.receipt_no} data-testid={`payments-row-${r.receipt_no}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-zinc-400">{r.date.slice(0, 10)}</td>
                      <td className="px-3 py-2 font-mono text-zinc-400">{r.receipt_no}</td>
                      <td className="px-3 py-2 tracker text-[9px]">{r.type.toUpperCase()}</td>
                      <td className="px-3 py-2 max-w-[260px] truncate">{r.item}</td>
                      <td className="px-3 py-2"><div>{r.member_name}</div><div className="text-zinc-500">{r.email}</div></td>
                      <td className="px-3 py-2 font-display text-sm">{money(r.amount, r.currency)}</td>
                      <td className="px-3 py-2 text-zinc-400">{money(r.platform_fee, r.currency)}</td>
                      <td className="px-3 py-2">{money(r.net, r.currency)}</td>
                      <td className="px-3 py-2 tracker text-[9px] text-zinc-400">{r.payment_method.toUpperCase()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.truncated && <p className="tracker text-[9px] text-zinc-500 px-3 py-2">Showing first 500 rows — the CSV has everything.</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
